import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Truck, Package, MapPin, Thermometer, Clock, 
  FileText, Download, RefreshCw, Search, Eye,
  CheckCircle, AlertTriangle, XCircle, Loader2, TrendingUp, QrCode, Send, MessageSquare, Phone,
  Play, StopCircle, Route
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, Area, ComposedChart
} from 'recharts';

const statusColors = {
  planned: 'bg-slate-500',
  in_progress: 'bg-blue-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500',
};

const deliveryStatusColors = {
  pending: 'bg-slate-500',
  in_transit: 'bg-blue-500',
  delivered: 'bg-green-500',
  failed: 'bg-red-500',
  returned: 'bg-orange-500',
};

const complianceStatusIcons = {
  compliant: { icon: CheckCircle, color: 'text-green-500' },
  non_compliant: { icon: XCircle, color: 'text-red-500' },
  partial: { icon: AlertTriangle, color: 'text-amber-500' },
  pending: { icon: Clock, color: 'text-slate-500 dark:text-slate-400' },
};

export default function TripsPage() {
  const { user, tenant } = useTrackOticAuth();
  const isBankProfile = ['BANK', 'BANK_P2'].includes(tenant?.profile_type) || ['AXIS', 'HDFC'].includes(tenant?.code);
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actingTripId, setActingTripId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [vanOptions, setVanOptions] = useState([]);
  const [createForm, setCreateForm] = useState({ vehicle_id: '', driver_name: '', notes: '' });
  const [generatingReport, setGeneratingReport] = useState(null);
  const [invoiceReports, setInvoiceReports] = useState({});
  const [invoiceTelemetry, setInvoiceTelemetry] = useState({});
  const [loadingTelemetry, setLoadingTelemetry] = useState({});
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  
  // QR Notification State
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedInvoiceForQR, setSelectedInvoiceForQR] = useState(null);
  const [qrPhoneNumber, setQrPhoneNumber] = useState('');
  const [sendSMS, setSendSMS] = useState(true);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [sendingQR, setSendingQR] = useState(false);
  const [qrSentStatus, setQrSentStatus] = useState({});

  // Delivery Confirmation State
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmInvoice, setConfirmInvoice] = useState(null);
  const [confirmTab, setConfirmTab] = useState('otp');
  const [generatingOTP, setGeneratingOTP] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [enteredOTP, setEnteredOTP] = useState('');
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [confirmationStatus, setConfirmationStatus] = useState({});

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await opsApi.getTrips(params);
      setTrips(response.data || []);
    } catch (error) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const openCreateTrip = async () => {
    setCreateForm({ vehicle_id: '', driver_name: '', notes: '' });
    try {
      const res = await opsApi.getVans();
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setVanOptions(rows);
    } catch (error) {
      setVanOptions([]);
    }
    setCreateOpen(true);
  };

  const handleSelectVan = (vehicleId) => {
    const van = vanOptions.find((v) => v.id === vehicleId);
    setCreateForm((f) => ({
      ...f,
      vehicle_id: vehicleId,
      driver_name: f.driver_name || van?.driver_now_name || '',
    }));
  };

  const handleCreateTrip = async () => {
    if (!createForm.vehicle_id) { toast.error('Select a van'); return; }
    const van = vanOptions.find((v) => v.id === createForm.vehicle_id);
    try {
      setCreating(true);
      await opsApi.createTrip({
        vehicle_id: createForm.vehicle_id,
        vehicle_number: van?.vehicle_number || null,
        driver_name: createForm.driver_name || null,
        notes: createForm.notes || null,
      });
      toast.success('Trip created — press Start when the van departs', { duration: 3000 });
      setCreateOpen(false);
      await fetchTrips();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create trip', { duration: 3000 });
    } finally {
      setCreating(false);
    }
  };

  const handleStartTrip = async (trip) => {
    try {
      setActingTripId(trip.id);
      await opsApi.startTrip(trip.id);
      toast.success(`Trip ${trip.trip_number} started`, { duration: 2000 });
      await fetchTrips();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start trip', { duration: 3000 });
    } finally {
      setActingTripId(null);
    }
  };

  const handleCompleteTrip = async (trip) => {
    try {
      setActingTripId(trip.id);
      const response = await opsApi.completeTrip(trip.id);
      const km = response?.data?.distance_km;
      toast.success(
        km != null
          ? `Trip ${trip.trip_number} completed — ${Number(km).toFixed(1)} km`
          : `Trip ${trip.trip_number} completed`,
        { duration: 3000 }
      );
      await fetchTrips();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to complete trip', { duration: 3000 });
    } finally {
      setActingTripId(null);
    }
  };

  const handleViewDetails = async (trip) => {
    setSelectedTrip(trip);
    setDetailsOpen(true);
    // Refresh the single trip so an in-progress run shows its live distance
    try {
      const fresh = await opsApi.getTrip(trip.id);
      if (fresh?.data) setSelectedTrip(fresh.data);
    } catch (error) {
      // keep the list copy
    }
    
    // Fetch existing reports for all invoices
    try {
      const response = await opsApi.getInvoiceReports({ trip_id: trip.id });
      const reports = {};
      (response.data || []).forEach(report => {
        reports[report.invoice_number] = report;
      });
      setInvoiceReports(reports);
    } catch (error) {
      console.error('Failed to load invoice reports for trip:', error);
    }
  };

  const handleGenerateReport = async (tripInvoiceId, invoiceNumber) => {
    try {
      setGeneratingReport(invoiceNumber);
      const response = await opsApi.generateInvoiceReport(tripInvoiceId);
      toast.success(`Report generated: ${response.data.report_number}`, { duration: 2000 });
      
      // Refresh reports
      if (selectedTrip) {
        const reportsResponse = await opsApi.getInvoiceReports({ trip_id: selectedTrip.id });
        const reports = {};
        (reportsResponse.data || []).forEach(report => {
          reports[report.invoice_number] = report;
        });
        setInvoiceReports(reports);
      }
    } catch (error) {
      toast.error('Failed to generate report', { duration: 3000 });
    } finally {
      setGeneratingReport(null);
    }
  };

  const handleDownloadPDF = (reportId) => {
    const url = opsApi.downloadInvoiceReportPDF(reportId);
    window.open(url, '_blank');
  };

  const handleToggleGraph = async (invoiceId, tripInvoiceId) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
      return;
    }
    
    setExpandedInvoice(invoiceId);
    
    // Fetch telemetry if not already loaded
    if (!invoiceTelemetry[invoiceId]) {
      setLoadingTelemetry(prev => ({ ...prev, [invoiceId]: true }));
      try {
        const response = await opsApi.getInvoiceTelemetry(tripInvoiceId);
        setInvoiceTelemetry(prev => ({ ...prev, [invoiceId]: response.data }));
      } catch (error) {
        toast.error('Failed to load temperature data');
      } finally {
        setLoadingTelemetry(prev => ({ ...prev, [invoiceId]: false }));
      }
    }
  };

  // QR Notification Handlers
  const handleOpenQRModal = (invoice) => {
    setSelectedInvoiceForQR(invoice);
    // Default to customer contact from invoice
    setQrPhoneNumber(invoice.customer_contact?.replace(/[^0-9]/g, '') || '');
    setSendSMS(true);
    setSendWhatsApp(true);
    setQrModalOpen(true);
  };

  const handleSendQR = async () => {
    if (!qrPhoneNumber || qrPhoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!sendSMS && !sendWhatsApp) {
      toast.error('Please select at least one channel (SMS or WhatsApp)');
      return;
    }

    try {
      setSendingQR(true);
      const response = await opsApi.sendQRNotification({
        invoice_id: selectedInvoiceForQR.invoice_id,
        phone_number: qrPhoneNumber,
        send_sms: sendSMS,
        send_whatsapp: sendWhatsApp,
      });
      
      toast.success(response.data.message, { duration: 4000 });
      
      // Update QR sent status
      setQrSentStatus(prev => ({
        ...prev,
        [selectedInvoiceForQR.invoice_id]: {
          sent: true,
          phone: qrPhoneNumber,
          channels: response.data.channels_sent,
        }
      }));
      
      setQrModalOpen(false);
    } catch (error) {
      toast.error('Failed to send QR notification');
    } finally {
      setSendingQR(false);
    }
  };

  // Delivery Confirmation Handlers
  const handleOpenConfirmModal = (invoice, tripId) => {
    setConfirmInvoice({ ...invoice, trip_id: tripId, trip_invoice_id: invoice.id });
    setConfirmTab('otp');
    setOtpData(null);
    setEnteredOTP('');
    setConfirmModalOpen(true);
  };

  const handleGenerateOTP = async () => {
    try {
      setGeneratingOTP(true);
      const res = await opsApi.generateDeliveryOTP({
        trip_invoice_id: confirmInvoice.trip_invoice_id,
        receiver_phone: confirmInvoice.customer_contact || null
      });
      setOtpData(res.data);
      if (res.data.already_confirmed) {
        setConfirmationStatus(prev => ({ ...prev, [confirmInvoice.trip_invoice_id]: true }));
        toast.info('Delivery already confirmed');
      } else {
        toast.success(`OTP sent to ${res.data.otp_sent_to || 'receiver'} (Mocked)`);      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate OTP');
    } finally {
      setGeneratingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!enteredOTP || enteredOTP.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }
    try {
      setVerifyingOTP(true);
      const res = await opsApi.verifyDeliveryOTP({
        trip_invoice_id: confirmInvoice.trip_invoice_id,
        otp: enteredOTP,
        receiver_name: null
      });
      toast.success(res.data.message);
      setConfirmationStatus(prev => ({ ...prev, [confirmInvoice.trip_invoice_id]: true }));
      setConfirmModalOpen(false);
      fetchTrips(); // Refresh trips to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setVerifyingOTP(false);
    }
  };

  const filteredTrips = trips.filter(trip => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      trip.trip_number?.toLowerCase().includes(term) ||
      trip.driver_name?.toLowerCase().includes(term) ||
      trip.vehicle_number?.toLowerCase().includes(term) ||
      trip.invoices?.some(inv => 
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.customer_name?.toLowerCase().includes(term)
      )
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="trips-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Trips & Invoice Reports</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            View trips and generate invoice-wise compliance reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchTrips}
            variant="outline"
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="refresh-trips-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={openCreateTrip}
            className="bg-teal-600 hover:bg-teal-500 text-white"
            data-testid="new-trip-btn"
          >
            <Truck className="w-4 h-4 mr-2" />
            New trip
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Truck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{trips.length}</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Total Trips</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Package className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {trips.reduce((sum, t) => sum + (t.invoices?.length || 0), 0)}
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Total Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {trips.filter(t => t.status === 'completed').length}
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {trips.filter(t => t.status === 'in_progress').length}
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
          <Input
            placeholder="Search trips, invoices, customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
            data-testid="trip-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Trips Table */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50">
                <TableHead className="text-slate-500 dark:text-slate-400">Trip #</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Device</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Driver</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Vehicle</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Invoices</TableHead>
                {isBankProfile ? (
                  <TableHead className="text-slate-500 dark:text-slate-400">Container</TableHead>
                ) : (
                  <TableHead className="text-slate-500 dark:text-slate-400">Temp Range</TableHead>
                )}
                <TableHead className="text-slate-500 dark:text-slate-400">Status</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Started</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Distance</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading trips...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredTrips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500 dark:text-slate-400">
                    No trips found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTrips.map((trip) => (
                  <TableRow 
                    key={trip.id} 
                    className="border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50"
                    data-testid={`trip-row-${trip.trip_number}`}
                  >
                    <TableCell className="font-medium text-slate-900 dark:text-white">
                      {trip.trip_number}
                    </TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300">
                      {trip.device_uid || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300">
                      {trip.driver_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300">
                      {trip.vehicle_number || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-600 text-slate-700 dark:text-slate-300">
                        {trip.invoices?.length || 0} invoices
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300">
                      {isBankProfile ? (
                        <Badge
                          variant="outline"
                          className={trip.bag_open
                            ? "border-red-500/40 text-red-300 bg-red-500/10"
                            : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"}
                        >
                          {trip.bag_open ? 'OPEN' : 'SEALED'}
                        </Badge>
                      ) : (
                        <span className="flex items-center gap-1">
                          <Thermometer className="w-4 h-4 text-blue-400" />
                          {trip.temp_min}°C - {trip.temp_max}°C
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[trip.status] || 'bg-slate-500'} text-slate-900 dark:text-white`}>
                        {trip.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 dark:text-slate-400 text-sm">
                      {formatDate(trip.actual_start || trip.planned_start)}
                    </TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-300 text-sm whitespace-nowrap">
                      {trip.distance_km != null ? (
                        <span className="flex items-center gap-1">
                          <Route className="w-3.5 h-3.5 text-teal-400" />
                          {Number(trip.distance_km).toFixed(1)} km
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {trip.status === 'planned' && (
                          <Button
                            size="sm"
                            disabled={actingTripId === trip.id}
                            onClick={() => handleStartTrip(trip)}
                            className="bg-teal-600 hover:bg-teal-500 text-white"
                            data-testid={`start-trip-${trip.trip_number}`}
                          >
                            {actingTripId === trip.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Play className="w-4 h-4 mr-1" />}
                            Start
                          </Button>
                        )}
                        {trip.status === 'in_progress' && (
                          <Button
                            size="sm"
                            disabled={actingTripId === trip.id}
                            onClick={() => handleCompleteTrip(trip)}
                            className="bg-blue-600 hover:bg-blue-500 text-white"
                            data-testid={`complete-trip-${trip.trip_number}`}
                          >
                            {actingTripId === trip.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <StopCircle className="w-4 h-4 mr-1" />}
                            Stop
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(trip)}
                          className="text-teal-400 hover:text-teal-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                          data-testid={`view-trip-${trip.trip_number}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {trip.qr_code && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/verify/${trip.qr_code}`, '_blank')}
                            className="text-blue-400 hover:text-blue-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                            data-testid={`qr-trip-${trip.trip_number}`}
                          >
                            <QrCode className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Trip Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-teal-400" />
              Trip Details: {selectedTrip?.trip_number}
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              View trip information and generate invoice-wise compliance reports
            </DialogDescription>
          </DialogHeader>

          {selectedTrip && (
            <Tabs defaultValue="invoices" className="mt-4">
              <TabsList className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                <TabsTrigger value="invoices" className="data-[state=active]:bg-teal-600">
                  Invoices ({selectedTrip.invoices?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="details" className="data-[state=active]:bg-teal-600">
                  Trip Details
                </TabsTrigger>
              </TabsList>

              <TabsContent value="invoices" className="mt-4 space-y-4">
                {selectedTrip.invoices?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    No invoices found for this trip
                  </div>
                ) : (
                  selectedTrip.invoices?.map((invoice, index) => {
                    const report = invoiceReports[invoice.invoice_number];
                    const ComplianceIcon = report 
                      ? complianceStatusIcons[report.compliance_status]?.icon || Clock
                      : Clock;
                    const complianceColor = report 
                      ? complianceStatusIcons[report.compliance_status]?.color || 'text-slate-500 dark:text-slate-400'
                      : 'text-slate-500 dark:text-slate-400';

                    return (
                      <Card key={invoice.invoice_id} className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                  #{invoice.sequence_number}
                                </Badge>
                                <span className="font-semibold text-slate-900 dark:text-white">
                                  {invoice.invoice_number}
                                </span>
                                <Badge className={`${deliveryStatusColors[invoice.status]} text-slate-900 dark:text-white`}>
                                  {invoice.status}
                                </Badge>
                              </div>
                              <div className="text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                  {invoice.customer_name}
                                </span>
                                {invoice.customer_address && (
                                  <p className="text-sm text-slate-500 dark:text-slate-400 ml-6">
                                    {invoice.customer_address}
                                  </p>
                                )}
                              </div>
                              {invoice.actual_delivery_at && (
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                  Delivered: {formatDate(invoice.actual_delivery_at)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {report ? (
                                <>
                                  <div className="flex items-center gap-1 mr-2">
                                    <ComplianceIcon className={`w-5 h-5 ${complianceColor}`} />
                                    <span className={`text-sm ${complianceColor}`}>
                                      {report.compliance_status?.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadPDF(report.id)}
                                    className="border-teal-600 text-teal-400 hover:bg-teal-600/20"
                                    data-testid={`download-pdf-${invoice.invoice_number}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    PDF
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleGenerateReport(invoice.id, invoice.invoice_number)}
                                  disabled={generatingReport === invoice.invoice_number || invoice.status === 'pending'}
                                  className="border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-700"
                                  data-testid={`generate-report-${invoice.invoice_number}`}
                                >
                                  {generatingReport === invoice.invoice_number ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <FileText className="w-4 h-4 mr-1" />
                                      Generate Report
                                    </>
                                  )}
                                </Button>
                              )}
                              {/* Send QR Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenQRModal(invoice)}
                                className={`border-purple-600 text-purple-400 hover:bg-purple-600/20 ${
                                  qrSentStatus[invoice.invoice_id]?.sent ? 'bg-purple-600/10' : ''
                                }`}
                                data-testid={`send-qr-${invoice.invoice_number}`}
                              >
                                <QrCode className="w-4 h-4 mr-1" />
                                {qrSentStatus[invoice.invoice_id]?.sent ? 'QR Sent' : 'Send QR'}
                              </Button>
                              {/* Confirm Delivery Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenConfirmModal(invoice, selectedTrip?.id)}
                                className={
                                  invoice.verified || confirmationStatus[invoice.id]
                                    ? 'border-green-600 text-green-400 bg-green-600/10'
                                    : 'border-teal-600 text-teal-400 hover:bg-teal-600/20'
                                }
                                disabled={invoice.verified || confirmationStatus[invoice.id]}
                                data-testid={`confirm-delivery-${invoice.invoice_number}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {invoice.verified || confirmationStatus[invoice.id] ? 'Confirmed' : 'Confirm Delivery'}
                              </Button>
                            </div>
                          </div>
                          {report && (
                            <div className="mt-3 pt-3 border-t border-slate-300 dark:border-slate-700">
                              {/* Stats Row */}
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-500 dark:text-slate-400">Min Temp:</span>
                                  <span className="ml-2 text-slate-900 dark:text-white">
                                    {report.temp_min_observed?.toFixed(1) || 'N/A'}°C
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500 dark:text-slate-400">Max Temp:</span>
                                  <span className="ml-2 text-slate-900 dark:text-white">
                                    {report.temp_max_observed?.toFixed(1) || 'N/A'}°C
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500 dark:text-slate-400">Avg Temp:</span>
                                  <span className="ml-2 text-slate-900 dark:text-white">
                                    {report.temp_avg_observed?.toFixed(1) || 'N/A'}°C
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500 dark:text-slate-400">Breaches:</span>
                                  <span className={`ml-2 ${report.breach_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    {report.breach_count || 0}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Show Graph Button */}
                              <div className="mt-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleGraph(invoice.invoice_number, invoice.id)}
                                  className="text-blue-400 hover:text-blue-300 hover:bg-slate-700 w-full justify-center"
                                  data-testid={`toggle-graph-${invoice.invoice_number}`}
                                >
                                  <TrendingUp className="w-4 h-4 mr-2" />
                                  {expandedInvoice === invoice.invoice_number ? 'Hide Temperature Graph' : 'Show Temperature Graph'}
                                </Button>
                              </div>
                              
                              {/* Temperature Graph */}
                              {expandedInvoice === invoice.invoice_number && (
                                <div className="mt-4 p-4 bg-white dark:bg-slate-900 rounded-lg">
                                  {loadingTelemetry[invoice.invoice_number] ? (
                                    <div className="flex items-center justify-center py-8">
                                      <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
                                      <span className="ml-2 text-slate-500 dark:text-slate-400">Loading temperature data...</span>
                                    </div>
                                  ) : invoiceTelemetry[invoice.invoice_number]?.data_points?.length > 0 ? (
                                    <>
                                      <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                                          <Thermometer className="w-4 h-4 text-blue-400" />
                                          Temperature History
                                        </h4>
                                        <div className="flex items-center gap-4 text-xs">
                                          <span className="flex items-center gap-1">
                                            <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                                            Temperature
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <div className="w-3 h-1 bg-green-500"></div>
                                            Min ({invoiceTelemetry[invoice.invoice_number]?.temp_min_allowed}°C)
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <div className="w-3 h-1 bg-red-500"></div>
                                            Max ({invoiceTelemetry[invoice.invoice_number]?.temp_max_allowed}°C)
                                          </span>
                                        </div>
                                      </div>
                                      <ResponsiveContainer width="100%" height={200}>
                                        <ComposedChart data={invoiceTelemetry[invoice.invoice_number].data_points}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                          <XAxis 
                                            dataKey="time" 
                                            stroke="#94a3b8" 
                                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                                            interval="preserveStartEnd"
                                          />
                                          <YAxis 
                                            stroke="#94a3b8" 
                                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                                            domain={['dataMin - 2', 'dataMax + 2']}
                                            tickFormatter={(val) => `${val}°C`}
                                          />
                                          <Tooltip
                                            contentStyle={{ 
                                              backgroundColor: '#1e293b', 
                                              border: '1px solid #334155',
                                              borderRadius: '8px',
                                              color: '#fff'
                                            }}
                                            formatter={(value, name) => [
                                              `${value?.toFixed(1)}°C`,
                                              name === 'temperature' ? 'Temperature' : name
                                            ]}
                                          />
                                          <ReferenceLine 
                                            y={invoiceTelemetry[invoice.invoice_number]?.temp_min_allowed} 
                                            stroke="#22c55e" 
                                            strokeDasharray="5 5"
                                            label={{ value: 'Min', position: 'left', fill: '#22c55e', fontSize: 10 }}
                                          />
                                          <ReferenceLine 
                                            y={invoiceTelemetry[invoice.invoice_number]?.temp_max_allowed} 
                                            stroke="#ef4444" 
                                            strokeDasharray="5 5"
                                            label={{ value: 'Max', position: 'left', fill: '#ef4444', fontSize: 10 }}
                                          />
                                          <Area
                                            type="monotone"
                                            dataKey="temperature"
                                            fill="url(#tempGradient)"
                                            stroke="none"
                                          />
                                          <Line 
                                            type="monotone" 
                                            dataKey="temperature" 
                                            stroke="#14b8a6" 
                                            strokeWidth={2}
                                            dot={(props) => {
                                              const { cx, cy, payload } = props;
                                              if (payload.is_breach) {
                                                return (
                                                  <circle 
                                                    cx={cx} 
                                                    cy={cy} 
                                                    r={4} 
                                                    fill="#ef4444" 
                                                    stroke="#fff"
                                                    strokeWidth={1}
                                                  />
                                                );
                                              }
                                              return null;
                                            }}
                                            activeDot={{ r: 6, fill: '#14b8a6', stroke: '#fff', strokeWidth: 2 }}
                                          />
                                          <defs>
                                            <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                                              <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                                              <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                                            </linearGradient>
                                          </defs>
                                        </ComposedChart>
                                      </ResponsiveContainer>
                                      <p className="text-xs text-slate-500 mt-2 text-center">
                                        {invoiceTelemetry[invoice.invoice_number].total_points} data points • Red dots indicate temperature breaches
                                      </p>
                                    </>
                                  ) : (
                                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                                      No temperature data available for this invoice
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-500 dark:text-slate-400">Trip Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Trip Number:</span>
                        <span className="text-slate-900 dark:text-white">{selectedTrip.trip_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Status:</span>
                        <Badge className={`${statusColors[selectedTrip.status]} text-slate-900 dark:text-white`}>
                          {selectedTrip.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Origin:</span>
                        <span className="text-slate-900 dark:text-white text-right max-w-[200px] truncate">
                          {selectedTrip.origin_address || 'N/A'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-500 dark:text-slate-400">Device & Driver</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Device UID:</span>
                        <span className="text-slate-900 dark:text-white">{selectedTrip.device_uid || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Driver:</span>
                        <span className="text-slate-900 dark:text-white">{selectedTrip.driver_name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Vehicle:</span>
                        <span className="text-slate-900 dark:text-white">{selectedTrip.vehicle_number || 'N/A'}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-500 dark:text-slate-400">
                        {isBankProfile ? 'Container Status' : 'Temperature Requirements'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {isBankProfile ? (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">Seal Status:</span>
                            <span className={selectedTrip.bag_open ? "text-red-400" : "text-emerald-400"}>
                              {selectedTrip.bag_open ? 'OPEN' : 'SEALED'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">Initial Weight:</span>
                            <span className="text-slate-900 dark:text-white">{selectedTrip.initial_weight ?? '—'} kg</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">Current Weight:</span>
                            <span className="text-slate-900 dark:text-white">{selectedTrip.current_weight ?? '—'} kg</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">Min Temp:</span>
                            <span className="text-blue-400">{selectedTrip.temp_min}°C</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400">Max Temp:</span>
                            <span className="text-red-400">{selectedTrip.temp_max}°C</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-500 dark:text-slate-400">Timeline</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Planned Start:</span>
                        <span className="text-slate-900 dark:text-white text-sm">{formatDate(selectedTrip.planned_start)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Actual Start:</span>
                        <span className="text-slate-900 dark:text-white text-sm">{formatDate(selectedTrip.actual_start)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">Actual End:</span>
                        <span className="text-slate-900 dark:text-white text-sm">{formatDate(selectedTrip.actual_end)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-slate-400">
                          Distance{selectedTrip.status === 'in_progress' ? ' (so far)' : ''}:
                        </span>
                        <span className="text-slate-900 dark:text-white text-sm flex items-center gap-1">
                          <Route className="w-3.5 h-3.5 text-teal-400" />
                          {selectedTrip.distance_km != null
                            ? `${Number(selectedTrip.distance_km).toFixed(1)} km`
                            : '—'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* New Trip Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-teal-400" />
              New trip
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Create a planned trip for a van. Distance is tracked from the van's GPS device between Start and Stop.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700 dark:text-slate-300">
                Van <span className="text-red-400">*</span>
              </Label>
              <select
                value={createForm.vehicle_id}
                onChange={(e) => handleSelectVan(e.target.value)}
                className="w-full mt-1 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2"
                data-testid="new-trip-van-select"
              >
                <option value="">— Select van —</option>
                {vanOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vehicle_number}{v.device_uid ? ` (${v.device_uid})` : ' (no device)'}
                  </option>
                ))}
              </select>
              {createForm.vehicle_id && !vanOptions.find((v) => v.id === createForm.vehicle_id)?.device_uid && (
                <p className="text-xs text-amber-500 mt-1">
                  This van has no GPS device mapped — distance will not be recorded.
                </p>
              )}
            </div>
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Driver name</Label>
              <Input
                value={createForm.driver_name}
                onChange={(e) => setCreateForm({ ...createForm, driver_name: e.target.value })}
                placeholder="Optional"
                className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                data-testid="new-trip-driver-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Notes</Label>
              <Input
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                placeholder="Optional"
                className="mt-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              className="border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTrip}
              disabled={creating || !createForm.vehicle_id}
              className="bg-teal-600 hover:bg-teal-500 text-white"
              data-testid="create-trip-btn"
            >
              {creating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                : 'Create trip'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Notification Modal */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-400" />
              Send Tracking QR
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Send tracking link via SMS and/or WhatsApp to the customer
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoiceForQR && (
            <div className="space-y-4">
              {/* Invoice Info */}
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Invoice:</span>
                  <span className="text-slate-900 dark:text-white font-medium">{selectedInvoiceForQR.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Customer:</span>
                  <span className="text-slate-900 dark:text-white">{selectedInvoiceForQR.customer_name}</span>
                </div>
                {selectedInvoiceForQR.customer_address && (
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400 text-sm">Delivery:</span>
                    <span className="text-slate-900 dark:text-white text-sm">{selectedInvoiceForQR.customer_address}</span>
                  </div>
                )}
              </div>

              {/* Phone Number Input */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 dark:text-slate-300">
                  Phone Number <span className="text-red-400">*</span>
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-l-md border border-r-0 border-slate-600">
                    +91
                  </span>
                  <Input
                    id="phone"
                    type="tel"
                    value={qrPhoneNumber}
                    onChange={(e) => setQrPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="9311748533"
                    className="bg-slate-100 dark:bg-slate-800 border-slate-600 text-slate-900 dark:text-white rounded-l-none"
                    maxLength={10}
                    data-testid="qr-phone-input"
                  />
                </div>
                {selectedInvoiceForQR.customer_contact && (
                  <p className="text-xs text-slate-500">
                    Invoice contact: {selectedInvoiceForQR.customer_contact}
                  </p>
                )}
              </div>

              {/* Channel Selection */}
              <div className="space-y-3">
                <Label className="text-slate-700 dark:text-slate-300">Send via</Label>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sms"
                      checked={sendSMS}
                      onCheckedChange={setSendSMS}
                      className="border-slate-600 data-[state=checked]:bg-blue-600"
                    />
                    <Label htmlFor="sms" className="flex items-center gap-1 text-slate-700 dark:text-slate-300 cursor-pointer">
                      <MessageSquare className="w-4 h-4 text-blue-400" />
                      SMS
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="whatsapp"
                      checked={sendWhatsApp}
                      onCheckedChange={setSendWhatsApp}
                      className="border-slate-600 data-[state=checked]:bg-green-600"
                    />
                    <Label htmlFor="whatsapp" className="flex items-center gap-1 text-slate-700 dark:text-slate-300 cursor-pointer">
                      <Phone className="w-4 h-4 text-green-400" />
                      WhatsApp
                    </Label>
                  </div>
                </div>
              </div>

              {/* Message Preview */}
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-sm">
                <p className="text-slate-500 dark:text-slate-400 text-xs mb-2">Message Preview:</p>
                <p className="text-slate-700 dark:text-slate-300">
                  🚚 Track0tic - Your shipment <span className="text-purple-400">{selectedInvoiceForQR.invoice_number}</span> is on the way!
                </p>
                <p className="text-blue-400 text-xs mt-1">
                  + Tracking link & QR code attached
                </p>
              </div>

              {/* QR Sent Status */}
              {qrSentStatus[selectedInvoiceForQR.invoice_id]?.sent && (
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm">
                    QR sent to {qrSentStatus[selectedInvoiceForQR.invoice_id].phone} via{' '}
                    {qrSentStatus[selectedInvoiceForQR.invoice_id].channels?.join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setQrModalOpen(false)}
              className="border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendQR}
              disabled={sendingQR || (!sendSMS && !sendWhatsApp) || qrPhoneNumber.length < 10}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="send-qr-btn"
            >
              {sendingQR ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send QR
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Confirmation Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              Confirm Delivery
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              {confirmInvoice?.invoice_number} — {confirmInvoice?.customer_name}
            </DialogDescription>
          </DialogHeader>

          {otpData?.already_confirmed ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-green-400 font-semibold">Already Confirmed</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                {otpData.verified_by && `By ${otpData.verified_by}`}
              </p>
            </div>
          ) : (
            <Tabs value={confirmTab} onValueChange={setConfirmTab}>
              <TabsList className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 w-full">
                <TabsTrigger value="otp" className="flex-1 data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                  OTP Verification
                </TabsTrigger>
                <TabsTrigger value="qr" className="flex-1 data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                  QR for Receiver
                </TabsTrigger>
              </TabsList>

              <TabsContent value="otp" className="space-y-4 mt-4">
                {!otpData ? (
                  <div className="text-center py-4">
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
                      Generate a 6-digit OTP. The receiver will share this code with you to confirm delivery.
                    </p>
                    <Button
                      onClick={handleGenerateOTP}
                      disabled={generatingOTP}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="generate-otp-btn"
                    >
                      {generatingOTP ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" />Generate & Send OTP</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 text-center">
                      <p className="text-slate-500 dark:text-slate-400 text-xs mb-1">OTP sent to receiver (MOCKED)</p>
                      <p className="text-3xl font-mono font-bold text-teal-400 tracking-widest" data-testid="otp-display">
                        {otpData.otp}
                      </p>
                      <p className="text-slate-500 text-xs mt-2">
                        Share this code with the receiver or ask them to provide it
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-slate-500 dark:text-slate-400 mb-1 block">Enter OTP from receiver</label>
                      <Input
                        value={enteredOTP}
                        onChange={(e) => setEnteredOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-center text-xl tracking-widest font-mono"
                        maxLength={6}
                        data-testid="enter-otp-input"
                      />
                    </div>

                    <Button
                      onClick={handleVerifyOTP}
                      disabled={verifyingOTP || enteredOTP.length !== 6}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="verify-otp-btn"
                    >
                      {verifyingOTP ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
                      ) : (
                        <><CheckCircle className="w-4 h-4 mr-2" />Verify & Confirm Delivery</>
                      )}
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="qr" className="space-y-4 mt-4">
                {!otpData ? (
                  <div className="text-center py-4">
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
                      Generate a QR confirmation link first. The receiver scans it to confirm delivery on their phone.
                    </p>
                    <Button
                      onClick={handleGenerateOTP}
                      disabled={generatingOTP}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="generate-qr-confirm-btn"
                    >
                      {generatingOTP ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><QrCode className="w-4 h-4 mr-2" />Generate Confirmation Link</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 text-center">
                      <QrCode className="w-16 h-16 text-teal-500 mx-auto mb-3" />
                      <p className="text-slate-900 dark:text-white text-sm font-medium mb-2">Confirmation Link Ready</p>
                      <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">
                        Share this link with the receiver or show the QR code:
                      </p>
                      <div className="bg-white dark:bg-slate-900 rounded p-2 text-teal-400 text-sm font-mono break-all" data-testid="confirm-link-display">
                        {window.location.origin}{otpData.confirm_url}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full border-teal-600 text-teal-400 hover:bg-teal-600/20"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}${otpData.confirm_url}`);
                        toast.success('Link copied to clipboard');
                      }}
                      data-testid="copy-confirm-link-btn"
                    >
                      Copy Link
                    </Button>
                    <p className="text-slate-500 text-xs text-center">
                      Receiver opens this link, sees shipment details & temperature compliance, then confirms receipt.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmModalOpen(false)}
              className="border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
